// AI.3B — Painel administrativo do Consultor IA (Overview, Funil, Custos,
// Latência, Qualidade). Privacidade: sem conteúdo de conversas por padrão.

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  AlertOctagon, Clock, DollarSign, Gauge, MessageSquare, Sparkles,
  TrendingUp, XCircle,
} from "lucide-react";
import { PageHeader, DataCard } from "@/components/admin/AdminPrimitives";
import { getAIAdminOverview, type AIAdminOverview } from "@/lib/ai/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/ia")({
  head: () => ({
    meta: [
      { title: "Consultor IA — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AIAdminPage,
});

const WINDOWS = [
  { d: 1, label: "24 h" },
  { d: 7, label: "7 dias" },
  { d: 14, label: "14 dias" },
  { d: 30, label: "30 dias" },
];

function fmtPct(n: number) { return `${Math.round(n * 100)}%`; }
function fmtUsd(n: number | null | undefined) {
  if (n == null) return "—";
  return n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
}
function fmtInt(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}
function fmtMs(n: number | null | undefined) {
  if (n == null) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;
}

function AIAdminPage() {
  const [days, setDays] = useState(14);
  const load = useServerFn(getAIAdminOverview);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["ai-admin-overview", days],
    queryFn: () => load({ data: { days } }),
    staleTime: 60_000,
  });

  return (
    <div>
      <PageHeader
        title="Consultor IA"
        description="Observabilidade: uso, funil, custos, latência e qualidade. Sem conteúdo de conversas."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-black/10 bg-white p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w.d}
              onClick={() => setDays(w.d)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                days === w.d ? "bg-[var(--navy)] text-white" : "text-[var(--gray-ink)] hover:bg-black/[0.03]"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03] disabled:opacity-50"
        >
          {isFetching ? "Atualizando…" : "Atualizar"}
        </button>
      </div>

      {isLoading && (
        <div className="rounded-xl border border-black/5 bg-white p-8 text-center text-sm text-[var(--gray-ink)]">
          Carregando métricas…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {(error as any)?.message ?? "Não foi possível carregar as métricas."}
        </div>
      )}

      {data && <AIOverviewContent data={data} />}
    </div>
  );
}

function AIOverviewContent({ data }: { data: AIAdminOverview }) {
  const t = data.totals;
  const funnel = data.funnel;

  const funnelSteps = [
    { key: "opened", label: "Conversas iniciadas", value: funnel.opened },
    { key: "messaged", label: "Mensagens do usuário", value: funnel.messaged },
    { key: "suggested", label: "Sugestões geradas", value: funnel.suggested },
    { key: "applied", label: "Sugestões aplicadas", value: funnel.applied },
    { key: "completedSection", label: "Seções concluídas", value: funnel.completedSection },
  ];

  return (
    <div className="space-y-6">
      {/* KPIs principais */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={MessageSquare} label="Conversas" value={fmtInt(t.conversations)} sub={`${fmtInt(t.messages)} mensagens`} />
        <Kpi icon={Sparkles} label="Sugestões aplicadas" value={fmtInt(t.applied + t.edited)} sub={`de ${fmtInt(t.suggestions)} · ${fmtPct(data.rates.applicationRate)}`} />
        <Kpi icon={DollarSign} label="Custo total" value={fmtUsd(data.cost.totalUsd)} sub={`${fmtInt(data.cost.totalTokens)} tokens`} />
        <Kpi icon={Gauge} label="Latência média" value={fmtMs(data.latency.avgMs)} sub={`p95 ${fmtMs(data.latency.p95)}`} tone={data.latency.avgMs && data.latency.avgMs > 8000 ? "warn" : "ok"} />
      </div>

      {/* Funil */}
      <DataCard>
        <div className="p-5">
          <h3 className="font-display text-base font-extrabold text-[var(--navy)]">Funil do consultor</h3>
          <p className="text-xs text-[var(--gray-ink)]">Do primeiro acesso à conclusão de seção.</p>
          <div className="mt-4 space-y-2">
            {funnelSteps.map((step, i) => {
              const max = funnelSteps[0]?.value || 1;
              const pct = Math.max(2, (step.value / max) * 100);
              const drop = i > 0 && funnelSteps[i - 1].value > 0
                ? Math.round((1 - step.value / funnelSteps[i - 1].value) * 100)
                : null;
              return (
                <div key={step.key}>
                  <div className="flex items-center justify-between text-[11px] text-[var(--gray-ink)]">
                    <span className="font-semibold text-[var(--navy)]">{step.label}</span>
                    <span>
                      <strong className="text-[var(--navy)]">{fmtInt(step.value)}</strong>
                      {drop != null && drop > 0 && <span className="ml-2 text-red-600">−{drop}%</span>}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                    <div className="h-full rounded-full bg-[var(--navy)]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DataCard>

      <div className="grid gap-3 lg:grid-cols-2">
        {/* Qualidade */}
        <DataCard>
          <div className="p-5">
            <h3 className="font-display text-base font-extrabold text-[var(--navy)]">Qualidade das sugestões</h3>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <QualityBox label="Aplicadas" value={t.applied} total={t.suggestions} tone="green" />
              <QualityBox label="Editadas" value={t.edited} total={t.suggestions} tone="navy" />
              <QualityBox label="Rejeitadas" value={t.rejected} total={t.suggestions} tone="gold" />
            </div>
            <div className="mt-4 flex items-center gap-3 text-[11px] text-[var(--gray-ink)]">
              <span className="inline-flex items-center gap-1"><AlertOctagon size={12} className="text-red-600" /> Conflitos bloqueados: <strong className="text-[var(--navy)]">{fmtInt(t.conflicts)}</strong></span>
              <span className="inline-flex items-center gap-1"><TrendingUp size={12} className="text-[var(--navy)]" /> Revisões: <strong className="text-[var(--navy)]">{fmtInt(t.reviews)}</strong></span>
            </div>
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Distribuição de confiança</p>
              <div className="mt-1.5 flex gap-1">
                <ConfBar label="alta" value={data.confidence.high} total={t.suggestions} tone="bg-[var(--green)]" />
                <ConfBar label="média" value={data.confidence.medium} total={t.suggestions} tone="bg-[var(--gold)]" />
                <ConfBar label="baixa" value={data.confidence.low} total={t.suggestions} tone="bg-black/30" />
              </div>
            </div>
          </div>
        </DataCard>

        {/* Custos e latência */}
        <DataCard>
          <div className="p-5">
            <h3 className="font-display text-base font-extrabold text-[var(--navy)]">Custo & performance</h3>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
              <MiniStat label="Custo/projeto" value={fmtUsd(data.cost.avgPerProjectUsd)} />
              <MiniStat label="Custo/sugestão aplicada" value={fmtUsd(data.cost.avgPerAppliedUsd)} />
              <MiniStat label="Tokens entrada" value={fmtInt(data.cost.inputTokens)} />
              <MiniStat label="Tokens saída" value={fmtInt(data.cost.outputTokens)} />
              <MiniStat label="p50 latência" value={fmtMs(data.latency.p50)} />
              <MiniStat label="p99 latência" value={fmtMs(data.latency.p99)} />
              <MiniStat label="Chamadas LLM" value={fmtInt(t.llmCalls)} />
              <MiniStat label="Erros" value={fmtInt(t.llmErrors)} sub={fmtPct(data.rates.errorRate)} tone={data.rates.errorRate > 0.05 ? "warn" : "ok"} />
            </dl>
            {data.costsByMode.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Custo por modo</p>
                <ul className="mt-1.5 space-y-1">
                  {data.costsByMode.slice(0, 5).map((m) => (
                    <li key={m.mode} className="flex items-center justify-between text-[12px]">
                      <span className="text-[var(--navy)]">{m.mode}</span>
                      <span className="text-[var(--gray-ink)]">{fmtUsd(m.costUsd)} · {fmtInt(m.calls)} chamadas</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DataCard>
      </div>

      {/* Erros recentes + top projetos */}
      <div className="grid gap-3 lg:grid-cols-2">
        <DataCard>
          <div className="p-5">
            <h3 className="font-display text-base font-extrabold text-[var(--navy)]">Erros recentes</h3>
            {data.recentErrors.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--gray-ink)]">Nenhum erro no período.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {data.recentErrors.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px]">
                    <XCircle size={12} className="mt-0.5 shrink-0 text-red-600" />
                    <div>
                      <p className="font-semibold text-[var(--navy)]">{e.type} · {e.mode}</p>
                      <p className="text-[11px] text-[var(--gray-ink)]">{e.action} · {new Date(e.at).toLocaleString("pt-BR")}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {data.errorsByType.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Por tipo</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {data.errorsByType.map((e) => (
                    <span key={e.type} className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                      {e.type} · {e.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DataCard>

        <DataCard>
          <div className="p-5">
            <h3 className="font-display text-base font-extrabold text-[var(--navy)]">Top projetos por custo</h3>
            {data.topProjects.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--gray-ink)]">Sem atividade suficiente.</p>
            ) : (
              <ul className="mt-3 space-y-1.5">
                {data.topProjects.map((p) => (
                  <li key={p.projectId} className="flex items-center justify-between text-[12px]">
                    <code className="rounded bg-black/[0.04] px-1.5 py-0.5 text-[10px] text-[var(--navy)]">
                      {p.projectId.slice(0, 8)}…
                    </code>
                    <span className="text-[var(--gray-ink)]">
                      {fmtUsd(p.costUsd)} · {fmtInt(p.calls)} chamadas · {fmtInt(p.applied)} aplicadas
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DataCard>
      </div>

      <p className="pt-2 text-[10px] text-[var(--gray-ink)]">
        <Clock size={10} className="mr-1 inline" /> Janela: {data.windowDays} dias · Privacidade: conteúdo textual de conversas não é exibido.
      </p>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone }: { icon: any; label: string; value: string; sub?: string; tone?: "ok" | "warn" }) {
  return (
    <DataCard>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <span className="rounded-lg bg-[var(--navy)]/8 p-2 text-[var(--navy)]">
            <Icon size={14} />
          </span>
          {tone === "warn" && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">atenção</span>
          )}
        </div>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</p>
        <p className="mt-0.5 font-display text-2xl font-extrabold text-[var(--navy)]">{value}</p>
        {sub && <p className="mt-0.5 text-[11px] text-[var(--gray-ink)]">{sub}</p>}
      </div>
    </DataCard>
  );
}

function QualityBox({ label, value, total, tone }: { label: string; value: number; total: number; tone: "green" | "navy" | "gold" }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const cls =
    tone === "green" ? "text-[var(--green)] bg-[var(--green)]/8"
    : tone === "gold" ? "text-[var(--gold)] bg-[var(--gold)]/12"
    : "text-[var(--navy)] bg-[var(--navy)]/8";
  return (
    <div className={`rounded-lg p-3 ${cls}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-0.5 font-display text-xl font-extrabold">{fmtInt(value)}</p>
      <p className="text-[11px]">{pct}%</p>
    </div>
  );
}

function ConfBar({ label, value, total, tone }: { label: string; value: number; total: number; tone: string }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div className="flex-1">
      <div className="h-2 w-full overflow-hidden rounded-full bg-black/5">
        <div className={`h-full ${tone}`} style={{ width: `${Math.max(3, pct)}%` }} />
      </div>
      <p className="mt-0.5 text-[10px] text-[var(--gray-ink)]">{label} · {fmtInt(value)}</p>
    </div>
  );
}

function MiniStat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "ok" | "warn" }) {
  return (
    <div className="rounded-lg border border-black/5 bg-[var(--gray-soft)]/40 p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</p>
      <p className={`mt-0.5 font-display text-base font-extrabold ${tone === "warn" ? "text-red-600" : "text-[var(--navy)]"}`}>{value}</p>
      {sub && <p className="text-[10px] text-[var(--gray-ink)]">{sub}</p>}
    </div>
  );
}
